import { EmptyState, Heading, Skeleton, Table, VStack } from "@chakra-ui/react";
import { FolderOpen } from "lucide-react";
import React from "react";

type Column = {
  key: string;
  label: string;
  header?: React.ReactNode;
  width?: string;
  minWidth?: number;
};

type Props = {
  isLoading: boolean;
  columns: readonly Column[];
  children: React.ReactNode;
  interactive?: boolean;
  fillHeight?: boolean;
  size?: "sm" | "md";
};

const GenericTable = ({
  isLoading,
  columns,
  children,
  interactive,
  fillHeight,
  size = "md",
}: Props): JSX.Element => {
  const hasData = React.Children.count(children) > 0;

  return (
    <Table.ScrollArea
      data-tour="page-table"
      rounded="lg"
      borderLeft="1.5px solid #ebecef80"
      bg="white"
      boxShadow="rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 1px 2px 0px"
      minH={fillHeight ? 0 : undefined}
    >
      <Table.Root size={size} stickyHeader interactive={interactive}>
        <Table.Header>
          <Table.Row bg="#fcfcfd" borderColor="#e5e7eb" border={1}>
            {columns.map((column) => (
              <Table.ColumnHeader
                key={column.key}
                fontWeight="semibold"
                color="gray.500"
                minWidth={column.minWidth}
              >
                {column.header ?? (
                  <Heading
                    size={size === "sm" ? "xs" : "sm"}
                    fontWeight="extrabold"
                  >
                    {column.label}
                  </Heading>
                )}
              </Table.ColumnHeader>
            ))}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {isLoading
            ? Array.from({ length: 4 }).map((_, index) => (
                <Table.Row key={index}>
                  <Table.Cell
                    padding="0.5rem"
                    border="none"
                    colSpan={columns.length}
                  >
                    <Skeleton height="20px" width="100%" />
                  </Table.Cell>
                </Table.Row>
              ))
            : children}
        </Table.Body>
      </Table.Root>

      {!isLoading && !hasData && (
        <EmptyState.Root>
          <EmptyState.Content>
            <EmptyState.Indicator>
              <FolderOpen size={80} />
            </EmptyState.Indicator>
            <VStack textAlign="center" gap="0.5rem">
              <EmptyState.Title>No hay datos</EmptyState.Title>
              <EmptyState.Description>
                No se encontraron resultados con los filtros aplicados
              </EmptyState.Description>
            </VStack>
          </EmptyState.Content>
        </EmptyState.Root>
      )}
    </Table.ScrollArea>
  );
};

export default GenericTable;
